import { API_CONFIG } from '../api/config';
import { getPDFFontStyle } from '../../utils/pdfFonts';
import { openDB } from 'idb';
import '../../assets/fonts/Andika-Bold-bold.js';
import '../../assets/fonts/Andika-BoldItalic-bolditalic.js';
import '../../assets/fonts/Andika-Italic-italic.js';
import '../../assets/fonts/Andika-Regular-normal.js';
import '../../assets/fonts/Baskervville-Italic-italic.js';
import '../../assets/fonts/Baskervville-Regular-normal.js';
import '../../assets/fonts/Quicksand-Bold-bold.js';
import '../../assets/fonts/Quicksand-Regular-normal.js';
import '../../assets/fonts/GreatVibes-Regular-normal.js';
import '../../assets/fonts/Bangers-Regular-normal.js';

// Use a standard import approach for the border image
// We'll define the URL that we'll use to load the border image at runtime
// Similar to how it's done in PreviewContent.jsx

// Initialize tracking variables for the border image
let borderImageLoaded = false;
let borderImageURL = null;

// Get the URL to the border image
function getBorderImagePath() {
  if (borderImageURL) {
    return borderImageURL;
  }
  
  try {
    // This matches the same approach used in PreviewContent.jsx
    // Using URL constructor with import.meta.url to resolve the asset path
    borderImageURL = new URL('../../assets/image-border/ImageAlphaChannel.png', import.meta.url).href;
    return borderImageURL;
  } catch (error) {
    console.error('Error creating URL for border image:', error);
    // Fallback to an absolute path if URL constructor fails
    return '/src/assets/image-border/ImageAlphaChannel.png';
  }
}

// Session-level denylist of URLs that returned non-image/blocked so we avoid retrying origin repeatedly
const badImageUrls = new Set();

// Fetch an image URL as a data URL (handles CORS by reading as Blob) with multi-proxy fallback
async function fetchImageAsDataUrl(url, timeoutMs = 6000) {
  const cleanUrl = (url || '').trim();
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  const toDataUrl = async (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const tryFetch = async (u, label) => {
    const res = await fetch(u, { signal: controller.signal, mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const ctype = res.headers.get('content-type') || '';
    if (ctype && !ctype.toLowerCase().startsWith('image/')) {
      throw new Error(`Non-image content-type: ${ctype}`);
    }
    const blob = await res.blob();
    const btype = blob.type || '';
    if (btype && !btype.toLowerCase().startsWith('image/')) {
      throw new Error(`Non-image blob type: ${btype}`);
    }
    const data = await toDataUrl(blob);
    if (label) console.info(`[ImageFetch] via ${label}`);
    return data;
  };
  try {
    // Try original URL first unless it's already known-bad
    if (!badImageUrls.has(cleanUrl)) {
      try {
        return await tryFetch(cleanUrl, 'origin');
      } catch (originErr) {
        // Mark this URL as bad for subsequent attempts in this session
        badImageUrls.add(cleanUrl);
        throw originErr;
      }
    } else {
      throw new Error('Origin previously failed; skipping to proxy');
    }
  } catch (e1) {
    try {
      // images.weserv.nl (no protocol) -> JPEG, width capped
      const u = new URL(cleanUrl);
      const noProto = `${u.host}${u.pathname}${u.search}`;
      const weserv = `https://images.weserv.nl/?url=${encodeURIComponent(noProto)}&w=1600&output=jpg`;
      return await tryFetch(weserv, 'weserv');
    } catch (e2) {
      try {
        // allorigins raw proxy
        const allorigins = `https://api.allorigins.win/raw?url=${encodeURIComponent(cleanUrl)}`;
        return await tryFetch(allorigins, 'allorigins');
      } catch (e3) {
        try {
          // isomorphic-git CORS proxy
          const corsIso = `https://cors.isomorphic-git.org/${cleanUrl}`;
          return await tryFetch(corsIso, 'cors.isomorphic-git');
        } catch (e4) {
          try {
            // jina.ai mirror (http)
            const u = new URL(cleanUrl);
            const jinaHttp = `https://r.jina.ai/http://${u.host}${u.pathname}${u.search}`;
            return await tryFetch(jinaHttp, 'jina-http');
          } catch (e5) {
            // jina.ai mirror (https)
            const u = new URL(cleanUrl);
            const jinaHttps = `https://r.jina.ai/https://${u.host}${u.pathname}${u.search}`;
            return await tryFetch(jinaHttps, 'jina-https');
          }
        }
      }
    }
  } finally {
    clearTimeout(id);
  }
}

// Function to store image in IndexedDB
async function storeImage(url, dataUrl) {
  try {
    const db = await openDB('imageCache', 1, {
      upgrade(db) {
        db.createObjectStore('images');
      },
    });
    await db.put('images', dataUrl, url);
  } catch (error) {
    console.error('Error storing image:', error);
  }
}

// Function to get image from IndexedDB
async function getCachedImage(url) {
  try {
    const db = await openDB('imageCache', 1);
    return await db.get('images', url);
  } catch (error) {
    console.error('Error getting cached image:', error);
    return null;
  }
}

/**
 * Helper to flatten transparency in images before adding to PDF
 * This is critical for Lulu PDF compliance
 * @param {string} imageUrl - URL or data URL of the image
 * @returns {Promise<string>} - Flattened image as data URL
 */
async function flattenImageTransparency(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      try {
        // Create a canvas to flatten the image
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw with white background to eliminate transparency
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF'; // White background
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        // Convert to JPEG (no transparency) with high quality
        const flattenedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
        resolve(flattenedDataUrl);
      } catch (err) {
        console.error('Error flattening image transparency:', err);
        resolve(imageUrl); // Fall back to original if flattening fails
      }
    };
    
    img.onerror = (err) => {
      console.warn('Failed to load image for flattening:', err);
      resolve(imageUrl); // Fall back to original if loading fails
    };
    
    img.src = imageUrl;
  });
}

/**
 * Renders an individual element to the PDF with proper scaling
 * @param {Object} doc - The jsPDF document
 * @param {Object} element - The element to render
 * @param {Object} options - Rendering options including scale factors
 * @returns {Promise<void>}
 */
export async function renderElementToPDF(doc, element, options = {}) {
  const { scale = 1, yScale = scale, debug = false } = options;

  if (!element?.type || !element.position || !element.size) {
    console.warn('Invalid element data:', element);
    return;
  }

  try {
    // Calculate position based on the element position and scale factors
    // Important: We now calculate directly from element.position to ensure proper positioning
    
    const x = element.position.x * scale;
    const y = element.position.y * scale;
    const width = element.size.width * scale;
    const height = element.size.height * scale;
    
    // Log the positioning details to help with debugging
    console.log(`Element ${element.id || 'unknown'} (${element.type}) rendering at PDF coords:`, {
      x, 
      y, 
      originalPosition: element.position,
      width,
      height,
      scale
    });
    
    // Draw element boundary in debug mode
    if (debug) {
      doc.setDrawColor(0, 128, 255); // Blue border for debugging
      doc.setLineWidth(0.5);
      doc.rect(x, y, width, height, 'S');
      
      // Add tiny element ID label for debugging
      doc.setFontSize(6);
      doc.setTextColor(0, 128, 255);
      doc.text(`${element.id || 'elem'} (${element.type})`, x + 2, y + 6);
    }

    switch (element.type) {
      case 'text':
        const fontSize = parseInt(element.style?.fontSize || '16') * scale;
        const textAlign = element.style?.textAlign || 'left';

        const fontFamilyCode = (_fontFamily, _fontWeight, _fontStyle) => {
          const isBold = _fontWeight === 'bold' || _fontWeight === '700' || _fontWeight === 700;
          const isItalic = _fontStyle === 'italic';
          
          // Map fonts to their available variants
          const fontMap = {
            'Bangers': 'Bangers-Regular', // Only Regular variant available
            'Andika': isBold && isItalic ? 'Andika-BoldItalic' : isBold ? 'Andika-Bold' : isItalic ? 'Andika-Italic' : 'Andika-Regular',
            'Quicksand': isBold ? 'Quicksand-Bold' : 'Quicksand-Regular',
            'Baskervville': isItalic ? 'Baskervville-Italic' : 'Baskervville-Regular',
            'Great Vibes': 'GreatVibes-Regular',
            'Times New Roman': isItalic ? 'Baskervville-Italic' : 'Baskervville-Regular',
            'Arial': isItalic ? 'Baskervville-Italic' : 'Baskervville-Regular',
          };
          
          return fontMap[_fontFamily] || 'Baskervville-Regular';
        };

        const fontStyleWeightCode = (_fontWeight, _fontStyle) => {
          // Map font weight and style to available variants
          const isBold = _fontWeight === 'bold' || _fontWeight === '700' || _fontWeight === 700;
          const isItalic = _fontStyle === 'italic';
          
          if (isBold && isItalic) return 'bolditalic';
          if (isBold) return 'bold';
          if (isItalic) return 'italic';
          return 'normal';
        };

        // Set font and size for text

        doc.setFont('times', 'normal');
        const fontFamily = fontFamilyCode(
          element?.style?.fontFamily,
          element?.style?.fontWeight,
          element?.style?.fontStyle
        );
        const fontStyleWeight = fontStyleWeightCode(
          element?.style?.fontWeight,
          element?.style?.fontStyle
        );

        console.log('Setting font for text element...', {
          testing: element?.style?.fontFamily,

          content: element?.content,
          fontFamily: fontFamily,
          fontWeight: fontStyleWeight,
        });

        const currentFont = doc.getFont();
        console.log('Font being used:', {
          fontName: currentFont.fontName
        });
        
        // For landscape-custom and US Trade sizes, apply exact font size
        const isExactSize = options.orientation === 'landscape' && 
                          (options.pageSize === 'landscape-custom' || options.pageSize === 'us-trade');
        
        // Apply a font correction factor to ensure text appears at the same size as design
        // No correction needed for exact size formats
        const fontCorrectionFactor = 1;
        const adjustedFontSize = fontSize * fontCorrectionFactor;
        
        // Use exact font data and set consistent styles
        doc.setFont(fontFamily, element.style?.fontStyle || 'normal');
        doc.setFontSize(adjustedFontSize);
        console.log(`Setting font: ${fontFamily}, size: ${adjustedFontSize} (original: ${fontSize})`);

        // Set text color if defined with improved color handling
        if (element.style?.color) {
          // Helper function to convert hex to RGB with better error handling
          const hexToRgb = (hex) => {
            try {
              // Remove # if present
              hex = hex.replace(/^#/, '');
              
              // Handle 3-character hex
              if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
              }
              
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              
              if (isNaN(r) || isNaN(g) || isNaN(b)) {
                console.warn(`Invalid hex color: ${hex}, defaulting to black`);
                return { r: 0, g: 0, b: 0 };
              }
              
              return { r, g, b };
            } catch (error) {
              console.error('Error parsing color:', error);
              return { r: 0, g: 0, b: 0 };
            }
          };
          
          // Handle different color formats (hex, rgb, rgba)
          let rgb = { r: 0, g: 0, b: 0 }; // Default to black
          
          const color = element.style.color.trim();
          
          if (color.startsWith('#')) {
            // Hex color format
            rgb = hexToRgb(color);
          } else if (color.startsWith('rgb')) {
            // RGB or RGBA format
            const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
            if (rgbMatch) {
              rgb = {
                r: parseInt(rgbMatch[1], 10),
                g: parseInt(rgbMatch[2], 10),
                b: parseInt(rgbMatch[3], 10)
              };
            }
          } else if (color === 'white') {
            rgb = { r: 255, g: 255, b: 255 };
          } else if (color === 'black') {
            rgb = { r: 0, g: 0, b: 0 };
          }
          
          // Set the text color in the PDF
          doc.setTextColor(rgb.r, rgb.g, rgb.b);
          console.log('Setting text color:', color, rgb);
        } else {
          // Default to black if no color specified
          doc.setTextColor(0, 0, 0);
        }

        // Calculate text width and height
        // Use the full element width, just like images do
        // The position adjustment in pageElements.js handles the page offset
        // Add 5% extension to the right to account for font metric differences between browser and PDF
        const textWidth = element.size.width * scale ;
        const textHeight = element.size.height * scale;
        
        // Position text based on our flipped coordinate system
        let textX = x;
        
        // Y position is already flipped in spreadRenderer, so no need to re-flip it here
        // For text, we need to add font size because jsPDF text positioning is from the baseline
        let textY = y + fontSize;

        // Handle horizontal alignment
        if (textAlign === 'center') {
          textX = x + textWidth / 2;
        } else if (textAlign === 'right') {
          textX = x + textWidth;
        }
        
        // Debug positioning
        console.log(`Text positioned at: (${textX}, ${textY}) with dimensions: ${textWidth}x${textHeight}`);


        // Handle vertical alignment
        const verticalAlign = element.style?.verticalAlign || 'top';
        const lineHeightMultiplier = parseFloat(element.style?.lineHeight || '1');
        const lineHeight = fontSize * lineHeightMultiplier;
        
        // First split by newlines to preserve explicit line breaks, then wrap each line to fit width
        const explicitLines = (element.content || '').split('\n');
        let lines = [];
        explicitLines.forEach((line) => {
          const wrappedLines = doc.splitTextToSize(line, textWidth);
          lines = lines.concat(wrappedLines);
        });
        
        // Post-process: rejoin lines that were split after punctuation when the next line would fit
        // This prevents jsPDF's aggressive punctuation-based wrapping (periods, commas, etc.)
        // BUT: Skip this rejoin logic if we have explicit newlines - preserve user's line breaks
        const rejoinedLines = [];
        const hasExplicitNewlines = explicitLines.length > 1;
        
        for (let i = 0; i < lines.length; i++) {
          const currentLine = lines[i];
          const trimmedCurrent = currentLine.trim();
          
          // Only apply rejoin logic if there are no explicit newlines
          // Check if current line ends with punctuation and next line exists
          // Match: period, comma, semicolon, colon, exclamation, question mark
          if (!hasExplicitNewlines && i < lines.length - 1 && /[.,;:!?]$/.test(trimmedCurrent)) {
            const nextLine = lines[i + 1];
            const combinedLine = currentLine + ' ' + nextLine;
            const combinedWidth = doc.getTextWidth(combinedLine);
            
            // If combined line fits within width, rejoin them
            if (combinedWidth <= textWidth) {
              rejoinedLines.push(combinedLine);
              i++; // Skip the next line since we've combined it
              continue;
            }
          }
          
          rejoinedLines.push(currentLine);
        }
        
        lines = rejoinedLines;
        
        console.log('Line height calculation:', {
          elementLineHeight: element.style?.lineHeight,
          lineHeightMultiplier,
          fontSize,
          calculatedLineHeight: lineHeight,
          elementId: element.id,
          originalLineCount: doc.splitTextToSize(element.content || '', textWidth).length,
          rejoinedLineCount: lines.length,
        });
        
        // Calculate how many lines can fit in the element's height
        const maxLinesInHeight = Math.floor(textHeight / lineHeight);
        
        // Clip lines to fit within the element's height (overflow: hidden behavior)
        const visibleLines = lines.slice(0, maxLinesInHeight);
        const totalTextHeight = visibleLines.length * lineHeight;

        console.log('Text layout:', {
          x: textX,
          y: textY,
          width: textWidth,
          height: textHeight,
          totalLines: lines.length,
          visibleLines: visibleLines.length,
          maxLinesInHeight,
          totalTextHeight,
        });

        // Adjust Y position based on vertical alignment
        if (verticalAlign === 'middle') {
          textY = y + textHeight / 2 - totalTextHeight / 2 + fontSize;
        } else if (verticalAlign === 'bottom') {
          textY = y + textHeight - totalTextHeight;
        }

        // Render text with proper alignment (only visible lines that fit in height)
        visibleLines.forEach((line, index) => {
          const lineY = textY + index * lineHeight;
          doc.text(line, textX, lineY, {
            align: textAlign,
            maxWidth: textWidth,
            renderingMode: 'fill', // Ensure text renders sharply for print
          });
          
          // Add underline if textDecoration is 'underline'
          if (element.style?.textDecoration === 'underline') {
            const textWidth = doc.getTextWidth(line);
            let underlineX = textX;
            
            // Adjust X position based on text alignment
            if (textAlign === 'center') {
              underlineX = textX - textWidth / 2;
            } else if (textAlign === 'right') {
              underlineX = textX - textWidth;
            }
            
            // Draw underline
            doc.setDrawColor(element.style?.color ? 0 : 0); // Use same color as text
            doc.setLineWidth(0.5);
            const underlineY = lineY + fontSize * 0.1; // Slightly below baseline
            doc.line(underlineX, underlineY, underlineX + textWidth, underlineY);
          }
        });
        
        // For debugging - add element box visualization if in debug mode
        if (options.debug) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(x, y, element.size.width * scale, element.size.height * yScale);
        }
        break;

      case 'image':
        try {
          // Helper function to draw image borders with different styles
          const drawImageBorder = (doc, x, y, width, height, borderColor, borderWidth, borderStyle, cornerRadius, scale, hexToRgb, drawRoundedRect) => {
            const borderRGB = hexToRgb(borderColor);
            doc.setDrawColor(borderRGB.r, borderRGB.g, borderRGB.b);
            doc.setLineWidth(borderWidth * scale);
            
            if (borderStyle === 'dotted') {
              // Dotted: circular dots spaced evenly around entire border
              const dotRadius = borderWidth * scale / 2;
              const spacing = borderWidth * scale * 3.5;
              const dotColor = hexToRgb(borderColor);
              
              doc.setFillColor(dotColor.r, dotColor.g, dotColor.b);
              
              // Calculate points along the entire perimeter (straight sides + corners)
              const points = [];
              
              // Function to add points along a line
              const addLinePoints = (startX, startY, endX, endY) => {
                const dx = endX - startX;
                const dy = endY - startY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const steps = Math.ceil(distance / spacing);
                
                for (let i = 0; i < steps; i++) {
                  const t = i / steps;
                  points.push({
                    x: startX + dx * t,
                    y: startY + dy * t
                  });
                }
              };
              
              // Function to add points along a corner arc
              const addArcPoints = (centerX, centerY, radius, startAngle, endAngle) => {
                const arcLength = Math.abs(endAngle - startAngle) * radius;
                const steps = Math.ceil(arcLength / spacing);
                
                for (let i = 0; i < steps; i++) {
                  const t = i / steps;
                  const angle = startAngle + (endAngle - startAngle) * t;
                  points.push({
                    x: centerX + radius * Math.cos(angle),
                    y: centerY + radius * Math.sin(angle)
                  });
                }
              };
              
              // Build perimeter: start at top-left, go clockwise
              // Top-left corner arc
              addArcPoints(x + cornerRadius, y + cornerRadius, cornerRadius, Math.PI, Math.PI * 1.5);
              // Top side
              addLinePoints(x + cornerRadius, y, x + width - cornerRadius, y);
              // Top-right corner arc
              addArcPoints(x + width - cornerRadius, y + cornerRadius, cornerRadius, Math.PI * 1.5, Math.PI * 2);
              // Right side
              addLinePoints(x + width, y + cornerRadius, x + width, y + height - cornerRadius);
              // Bottom-right corner arc
              addArcPoints(x + width - cornerRadius, y + height - cornerRadius, cornerRadius, 0, Math.PI * 0.5);
              // Bottom side
              addLinePoints(x + width - cornerRadius, y + height, x + cornerRadius, y + height);
              // Bottom-left corner arc
              addArcPoints(x + cornerRadius, y + height - cornerRadius, cornerRadius, Math.PI * 0.5, Math.PI);
              // Left side
              addLinePoints(x, y + height - cornerRadius, x, y + cornerRadius);
              
              // Draw all dots
              points.forEach(point => {
                doc.circle(point.x, point.y, dotRadius, 'F');
              });
            } 
            else if (borderStyle === 'dashed') {
              // Dashed: with corner handling - corners should be solid/rounded, straight parts dashed
              // Use offset to start/end dashes in the middle of straight segments, avoiding corners
              const dashLen = Math.max(5, borderWidth * 2.5);
              const gapLen = Math.max(3, borderWidth * 1.5);
              const cornerOffset = cornerRadius * 1.5; // Start offset after corners
              doc.setLineDashPattern([dashLen, gapLen], cornerOffset);
              drawRoundedRect(doc, x, y, width, height, cornerRadius, 'S');
              doc.setLineDashPattern([], 0);
            }
            else if (borderStyle === 'double') {
              // Double: two strokes with width based on element border width
              const strokeW = 1 + (borderWidth / 5); // Scale stroke width with border width
              const gapSize = 2 + (borderWidth / 10); // Gap between borders
              
              // Inner stroke - positioned at image edge/boundary
              doc.setLineWidth(strokeW);
              drawRoundedRect(doc, x, y, width, height, cornerRadius, 'S');
              
              // Outer stroke - positioned outside with gap
              const outerOffset = gapSize + strokeW;
              drawRoundedRect(doc, x - outerOffset, y - outerOffset, width + (outerOffset * 2), height + (outerOffset * 2), cornerRadius, 'S');
            }
            else {
              // Solid: default
              drawRoundedRect(doc, x, y, width, height, cornerRadius, 'S');
            }
          };
          
          if (options?.omitImages) {
            // Draw a lightweight placeholder and skip actual image work
            doc.setDrawColor(220, 220, 220);
            doc.setFillColor(245, 245, 245);
            doc.rect(x, y, element.size.width * scale, element.size.height * yScale, 'FD');
            if (debug) {
              doc.setFontSize(8);
              doc.setTextColor(150, 150, 150);
              doc.text('Image omitted', x + 4, y + 10);
            }
            break;
          }
          const rawSrc = (element.src || '').trim();
          const isCrossOrigin = (() => {
            try {
              const u = new URL(rawSrc, window.location.href);
              return u.origin !== window.location.origin;
            } catch (_) {
              return false;
            }
          })();
          console.log('Processing image:', {
            src: rawSrc,
            elementId: element.id,
            imagePrompt: element.imagePrompt,
            hasSource: !!element.src
          });

          // Per-image time budget to prevent stalls on bad/corrupted images
          const startedAt = Date.now();
          const budgetMs = 3500; // hard cap per image
          const remainingBudget = () => Math.max(500, budgetMs - (Date.now() - startedAt));
          const imageBudgetExceeded = () => (Date.now() - startedAt) > budgetMs;

          // Track if we should abort processing this image after an internal failure
          let abortRender = false;

          // If no image source is available, skip rendering this element entirely
          if (!element.src) {
            console.log('No image source found, skipping image element');
            return;
          } else {
            let img;
            // Ensure loadFailed is in scope for all branches (including preloaded images)
            let loadFailed = false;
            
            // Check if we have a preloaded image (from concurrent loading optimization)
            if (element._preloadedImage) {
              console.log('🚀 Using preloaded image - skipping individual load');
              img = element._preloadedImage;
            } else {
              // Fallback to individual loading (legacy behavior)
              console.log('⚠️ No preloaded image found, loading individually');
              img = new Image();

              // Load the image
              // If cross-origin or forcing dataURL, try that path first to avoid CORS/taint issues
              let loadedFromDataUrl = false;
              if (options?.forceDataURLImages || isCrossOrigin) {
                try {
                  const dataUrl = await fetchImageAsDataUrl(rawSrc, 12000);
                  const tmp = new Image();
                  await new Promise((resolve, reject) => {
                    const t = setTimeout(() => reject(new Error('dataURL image load timeout')), 4000);
                    tmp.onload = () => { clearTimeout(t); resolve(); };
                    tmp.onerror = (e) => { clearTimeout(t); reject(e); };
                    tmp.src = dataUrl;
                  });
                  img = tmp;
                  loadedFromDataUrl = true;
                  console.log('Using forceDataURLImages path for image');
                } catch (forceErr) {
                  console.warn('forceDataURLImages fetch failed; falling back to HTMLImage load:', forceErr);
                }
              }
              if (!loadedFromDataUrl) {
                await new Promise((resolve, reject) => {
                  const timeoutId = setTimeout(() => {
                    console.warn('Image load timeout:', element.src);
                    reject(new Error('Image load timeout'));
                  }, 8000); // 8s timeout: allow slower images to load

                  img.onload = () => {
                    clearTimeout(timeoutId);
                    console.log('Image loaded successfully');
                    resolve();
                  };
                  
                  img.onerror = (e) => {
                    clearTimeout(timeoutId);
                    console.error('Image load error:', e);
                    reject(e);
                  };
                  
                  // Set the source to start loading
                  img.src = rawSrc;
                }).catch(err => {
                  console.warn('Image load failed, will attempt dataURL fallback:', err);
                  loadFailed = true;
                  if (options?.fastSkipBrokenImages !== false) {
                    abortRender = true;
                  }
                });
              } else {
                console.log('Skipping HTML image load; dataURL image already loaded');
              }
            }

            if (abortRender) { return; }

            // If image loaded successfully, add it to the PDF
            try {
              // Try to recover a broken/cors-blocked image by fetching as dataURL first
              let imageToUse = img;
              if (!imageToUse || !imageToUse.complete || loadFailed) {
                if (imageBudgetExceeded()) {
                  console.warn('Per-image budget exceeded before early dataURL fallback; skipping');
                } else {
                try {
                  console.warn('Image element unavailable/broken. Attempting fetch->dataURL fallback early...');
                  const dataUrl = await fetchImageAsDataUrl(rawSrc, remainingBudget());
                  const tmp = new Image();
                  await new Promise((resolve, reject) => {
                    const t = setTimeout(() => reject(new Error('dataURL image load timeout')), Math.min(2000, remainingBudget()));
                    tmp.onload = () => { clearTimeout(t); resolve(); };
                    tmp.onerror = (e) => { clearTimeout(t); reject(e); };
                    tmp.src = dataUrl;
                  });
                  imageToUse = tmp;
                  console.log('Using dataURL-fetched image due to initial load/CORS failure');
                } catch (earlyFetchErr) {
                  console.error('Early dataURL fallback failed; will try later fallbacks:', earlyFetchErr);
                  // Mark this URL as bad to avoid repeated origin/proxy attempts
                  badImageUrls.add(rawSrc);
                  // Proceed to later fallbacks (dataURL again or placeholders)
                }
                }
              }

              // For Lulu compatibility - flatten image transparency before adding to PDF
              // Only if the compatibility option is enabled and it's not already a JPEG
              let shouldFlatten = false;
              if (options?.luluCompatibility?.flattenTransparency && imageToUse && imageToUse.src) {
                const srcLower = (imageToUse.src || '').toLowerCase();
                shouldFlatten = !srcLower.endsWith('.jpg') && !srcLower.endsWith('.jpeg') && !srcLower.startsWith('data:image/jpeg');
              }
              
              if (shouldFlatten) {
                try {
                  console.log(`Flattening image transparency for Lulu compatibility (ID: ${element.id})`);
                  const flattenedImageDataUrl = await flattenImageTransparency(imageToUse.src);
                  
                  // Create a new image with the flattened (transparency-free) data
                  const flattenedImg = new Image();
                  await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                      console.warn('Flattened image load timeout, using original image');
                      reject();
                    }, 3000);
                    
                    flattenedImg.onload = () => {
                      clearTimeout(timeout);
                      resolve();
                    };
                    
                    flattenedImg.onerror = () => {
                      clearTimeout(timeout);
                      console.warn('Error loading flattened image, using original');
                      reject();
                    };
                    
                    flattenedImg.src = flattenedImageDataUrl;
                  }).then(() => {
                    imageToUse = flattenedImg;
                    console.log('Successfully flattened image transparency');
                  }).catch(() => {
                    // On any error, fall back to original image
                    console.warn('Using original image due to flattening error');
                  });
                } catch (flattenError) {
                  console.error('Image flattening failed:', flattenError);
                  // Continue with original image
                }
              } else {
                console.log(`Skipping transparency flattening for ${element.id} (not needed or already JPEG)`);
              }
              
              // Special handling for landscape-custom format
              const isLandscapeCustom = options.orientation === 'landscape' && 
                                       options.pageSize === 'landscape-custom';
              
              let imageX = x;
              let imageY = y;
              let imageWidth = element.size.width * scale;
              let imageHeight = element.size.height * yScale;
              
              // For landscape-custom (12.382 × 9.25 inches), apply precise positioning
              if (isLandscapeCustom) {
                // Use exact dimensions for landscape-custom format
                const exactPDFWidth = 891.5; // 12.382 * 72 points
                const exactPDFHeight = 666;  // 9.25 * 72 points
                
                // No correction factors - use exact positioning from spreadRenderer
                // This ensures consistency between the design view and PDF output
                if (options.flippedY) {
                  // If Y is already flipped in spreadRenderer.js, use the coordinates directly
                  // Use exact dimensions to maintain proportions
                  imageX = x;
                  imageY = y;
                  imageWidth = element.size.width * scale;
                  imageHeight = element.size.height * yScale;
                }
                
                console.log(`Precise image positioning for landscape-custom (ID: ${element.id}):`, {
                  position: { x: imageX, y: imageY },
                  dimensions: { width: imageWidth, height: imageHeight },
                  scale: { x: scale, y: yScale },
                  originalSize: element.size,
                  orientation: options.orientation,
                  pageSize: options.pageSize
                });
              } else {
                // Standard positioning for other formats
                imageWidth = element.size.width * scale;
                imageHeight = element.size.height * yScale;
              }
              
              // NO BORDER - image only

              // Yield to event loop to keep UI responsive before heavy image work
              // eslint-disable-next-line no-await-in-loop
              await new Promise((r) => setTimeout(r, 0));

              // Prefer adding a compressed JPEG dataURL at the exact target size to avoid
              // jsPDF re-encoding very large source images which can cause stalls.
              let added = false;
              let taintedCanvas = false;
              try {
                const targetW = Math.max(1, Math.round(imageWidth));
                const targetH = Math.max(1, Math.round(imageHeight));

                // Prefer direct data URL path for cross-origin or already data URL
                const preferDirectDataUrl = (imageToUse?.src || '').startsWith('data:image') || isCrossOrigin || options?.forceDataURLImages === true;
                if (!imageBudgetExceeded() && preferDirectDataUrl && !badImageUrls.has(rawSrc)) {
                  try {
                    let dataSrc = imageToUse?.src || '';
                    if (!dataSrc.startsWith('data:image')) {
                      // Convert remote URL to data URL before adding to avoid CORS/taint
                      dataSrc = await fetchImageAsDataUrl(rawSrc, remainingBudget());
                    }
                    // Ensure this is an image data URL; bail if proxy returned non-image content
                    if (!dataSrc.startsWith('data:image/')) {
                       throw new Error('Non-image data URL returned');
                     }
                     const fmt = (dataSrc.startsWith('data:image/jpeg') || dataSrc.startsWith('data:image/jpg')) ? 'JPEG' : 'PNG';
                     
                     // FIX: Calculate proper aspect ratio for direct data URL images too
                     const imgProps = doc.getImageProperties(dataSrc);
                     const imgAspectRatio = imgProps.width / imgProps.height;
                     
                     // Scale image to 85% of frame width, maintaining aspect ratio
                     const imageWidth = targetW * 0.95;
                     const imageHeight = imageWidth / imgAspectRatio;
                     
                     // Center in frame
                     const centeredImageX = imageX + (targetW - imageWidth) / 2;
                     const centeredImageY = imageY + (targetH - imageHeight) / 2;
                     
                     doc.addImage(
                       dataSrc,
                       fmt,
                       centeredImageX,
                       centeredImageY,
                       imageWidth,
                       imageHeight,
                       undefined,
                       'FAST'
                      );
                      added = true;
                      
                       // Draw border around image if enabled
                       if (element.hasBorder) {
                         try {
                           const borderColor = element.borderColor || '#ffffff';
                           const borderWidth = (element.borderWidth || 0) + 8;
                           const borderOpacity = element.borderOpacity !== undefined ? element.borderOpacity / 100 : 1.0;
                           const borderRadius = element.borderRadius || 0;
                           const borderStyle = element.borderStyle || 'solid';
                           
                           doc.saveGraphicsState();
                           
                           // Set opacity if supported
                           try {
                             if (doc && typeof doc.setGState === 'function' && doc.GState) {
                               doc.setGState(new doc.GState({ opacity: borderOpacity }));
                             }
                           } catch (_) {}
                           
                           doc.setFillColor(255, 255, 255, 0); // No fill
                           
                           // Draw border with style support
                           const cornerRadius = borderRadius * scale;
                           drawImageBorder(doc, centeredImageX, centeredImageY, imageWidth, imageHeight, borderColor, borderWidth, borderStyle, cornerRadius, scale, hexToRgb, drawRoundedRect);
                           
                           doc.restoreGraphicsState();
                           console.log('[PDF Border] Drew border around direct image:', { borderColor, borderWidth, borderStyle, borderRadius });
                         } catch (borderErr) {
                           console.warn('Failed to draw image border:', borderErr);
                         }
                       }
                      
                      console.log('[PDF Direct DataURL] Added at native AR, small size, centered:', {
                        size: { w: imageWidth, h: imageHeight },
                        position: { x: centeredImageX, y: centeredImageY },
                        ar: imgAspectRatio
                      });
                   } catch (directErr) {
                    console.warn('Direct addImage of data URL failed, will try canvas path:', directErr);
                  }
                }

                // If not already added, attempt canvas conversion but cap canvas pixel count ~3MP
                if (imageBudgetExceeded()) throw new Error('Per-image budget exceeded before canvas path');
                const maxPixels = 1732 * 1732; // ~3MP cap
                const targetPixels = Math.max(1, targetW * targetH);
                const scaleCap = Math.min(1, Math.sqrt(maxPixels / targetPixels));
                const canvasW = Math.max(1, Math.floor(targetW * scaleCap));
                const canvasH = Math.max(1, Math.floor(targetH * scaleCap));

                 if (!added && imageToUse) {
                   console.log('[PDF Canvas] Creating canvas for image with borderRadius:', element.borderRadius);
                    const canvas = document.createElement('canvas');
                    canvas.width = canvasW;
                    canvas.height = canvasH;
                    const ctx = canvas.getContext('2d', { alpha: true });
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';

                    // Canvas starts transparent - NO white background
                    // Image will be centered and transparent areas will show underlying elements/page

                    const imgBorderRadius = element.borderRadius || 8;
                    console.log('[PDF Canvas] imgBorderRadius:', imgBorderRadius, 'canvasW:', canvasW, 'canvasH:', canvasH);
                    const imgBorderColor = element.borderColor || '#ffffff';
                    const imgBorderWidth = element.borderWidth || 8;
                    const imgBorderStyle = element.borderStyle || 'solid';
                    const srcW = imageToUse.naturalWidth || imageToUse.width || targetW;
                    const srcH = imageToUse.naturalHeight || imageToUse.height || targetH;
                    // Force contain mode for proper aspect ratio preservation in PDF
                    // This ensures the image layout matches the design canvas
                    const fit = 'contain';

                   let drawnW, drawnH; // Track actual drawn image dimensions for aspect ratio preservation

                  // Helper function to draw rounded rectangle path
                  const drawRoundedRectPath = (x, y, w, h, r) => {
                    ctx.beginPath();
                    ctx.moveTo(x + r, y);
                    ctx.lineTo(x + w - r, y);
                    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
                    ctx.lineTo(x + w, y + h - r);
                    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
                    ctx.lineTo(x + r, y + h);
                    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
                    ctx.lineTo(x, y + r);
                    ctx.quadraticCurveTo(x, y, x + r, y);
                    ctx.closePath();
                  };

                  if (fit === 'cover') {
                    // Scale up to cover entire target and crop source from center (into capped canvas)
                    const scale = Math.max(canvasW / srcW, canvasH / srcH);
                    drawnW = Math.max(1, Math.round(srcW * scale));
                    drawnH = Math.max(1, Math.round(srcH * scale));
                    const dx = Math.floor((canvasW - drawnW) / 2);
                    const dy = Math.floor((canvasH - drawnH) / 2);
                    ctx.drawImage(imageToUse, 0, 0, srcW, srcH, dx, dy, drawnW, drawnH);
                   } else {
                     // Default: contain (preserve entire image, letterbox/pillarbox)
                     const scale = Math.min(canvasW / srcW, canvasH / srcH);
                     drawnW = Math.max(1, Math.round(srcW * scale));
                     drawnH = Math.max(1, Math.round(srcH * scale));
                     const dx = Math.floor((canvasW - drawnW) / 2);
                     const dy = Math.floor((canvasH - drawnH) / 2);
                     console.log('[PDF Contain] Rendering image with contain mode:', {
                       srcW, srcH,
                       canvasW, canvasH,
                       scale,
                       drawnW, drawnH,
                       offsetX: dx, offsetY: dy,
                       elementId: element.id
                     });
                      ctx.drawImage(imageToUse, 0, 0, srcW, srcH, dx, dy, drawnW, drawnH);
                    }
                   
                   // Draw border frame ON TOP of the image in the canvas

                    // CRITICAL FIX: Crop the canvas to ONLY the drawn image area
                    // Don't include the transparent canvas around it - we only want the actual image
                    const croppedCanvas = document.createElement('canvas');
                    croppedCanvas.width = drawnW;
                    croppedCanvas.height = drawnH;
                    const croppedCtx = croppedCanvas.getContext('2d', { alpha: true });
                    
                    // Copy only the drawn image portion from the large canvas
                    let dx = 0, dy = 0;
                    if (fit === 'cover') {
                      const scale = Math.max(canvasW / srcW, canvasH / srcH);
                      dx = Math.floor((canvasW - drawnW) / 2);
                      dy = Math.floor((canvasH - drawnH) / 2);
                    } else {
                      const scale = Math.min(canvasW / srcW, canvasH / srcH);
                      dx = Math.floor((canvasW - drawnW) / 2);
                      dy = Math.floor((canvasH - drawnH) / 2);
                    }
                    
                    croppedCtx.drawImage(canvas, dx, dy, drawnW, drawnH, 0, 0, drawnW, drawnH);
                    const dataUrl = croppedCanvas.toDataURL('image/png', 1.0);

                    // SIMPLE PATTERN: Get image properties and calculate height from fixed width
                    // This ensures perfect aspect ratio - no stretching or cropping
                    const imgProps = doc.getImageProperties(dataUrl);
                    const imgAspectRatio = imgProps.width / imgProps.height;
                    
                    // Scale image to 85% of frame width, maintaining aspect ratio
                    const targetImageWidth = targetW * 0.95;
                    const targetImageHeight = targetImageWidth / imgAspectRatio;
                    
                    // Center image in the element frame
                    const pdfImageX = imageX + (targetW - targetImageWidth) / 2;
                    const pdfImageY = imageY + (targetH - targetImageHeight) / 2;
                    
                    console.log('[PDF Image] Simple pattern - fixed width, calculated height:', {
                      elementFrame: { x: imageX, y: imageY, w: targetW, h: targetH },
                      imageProperties: { w: imgProps.width, h: imgProps.height, ar: imgAspectRatio },
                      pdfImageSize: { w: targetImageWidth, h: targetImageHeight },
                      pdfImagePosition: { x: pdfImageX, y: pdfImageY },
                      elementId: element.id
                    });

                    // Add image at correct aspect ratio, small size, centered in frame
                    doc.addImage(
                      dataUrl,
                      'PNG',
                      pdfImageX,
                      pdfImageY,
                      targetImageWidth,
                      targetImageHeight,
                      undefined,
                      'FAST'
                    );
                    
                    // Draw border around image if enabled
                    if (element.hasBorder) {
                      try {
                        const borderColor = element.borderColor || '#ffffff';
                        const borderWidth = (element.borderWidth || 8); // Update default borderWidth to 8
                        const borderOpacity = element.borderOpacity !== undefined ? element.borderOpacity / 100 : 1.0;
                        const borderRadius = element.borderRadius || 0;
                        const borderStyle = element.borderStyle || 'solid';
                        
                        doc.saveGraphicsState();
                        
                        // Set opacity if supported
                        try {
                          if (doc && typeof doc.setGState === 'function' && doc.GState) {
                            doc.setGState(new doc.GState({ opacity: borderOpacity }));
                          }
                        } catch (_) {}
                        
                        doc.setFillColor(255, 255, 255, 0); // No fill
                        
                        // Draw border with style support
                        const cornerRadius = borderRadius * scale;
                        drawImageBorder(doc, pdfImageX, pdfImageY, targetImageWidth, targetImageHeight, borderColor, borderWidth, borderStyle, cornerRadius, scale, hexToRgb, drawRoundedRect);
                        
                        doc.restoreGraphicsState();
                        console.log('[PDF Border] Drew border around canvas image:', { borderColor, borderWidth, borderStyle, borderRadius });
                      } catch (borderErr) {
                        console.warn('Failed to draw image border:', borderErr);
                      }
                    }
                    
                    console.log('[PDF Image Added] Centered at native aspect ratio:', {
                      position: { x: pdfImageX, y: pdfImageY },
                      size: { w: targetImageWidth, h: targetImageHeight },
                      transparent: true,
                      elementId: element.id
                    });
                  
                  added = true;
                  console.log('Image added to PDF via pre-compressed JPEG (capped canvas) with aspect ratio preserved');
                }
              } catch (compressErr) {
                console.warn('Image pre-compression skipped due to error or CORS:', compressErr);
                taintedCanvas = true;
                if (options?.fastSkipBrokenImages !== false) {
                  console.warn('Fast-skip enabled. Skipping image after canvas/compression error.');
                  abortRender = true;
                }
              }

              if (abortRender) { return; }

              // Note: do not draw placeholder yet; try dataURL fallback first

              if (!added) {
                // Fallback: try fetching as data URL to bypass CORS-tainted canvas
                try {
                  if (imageBudgetExceeded()) throw new Error('Per-image budget exceeded before late dataURL');
                  const srcStr = (imageToUse && imageToUse.src) ? imageToUse.src : rawSrc;
                  if (badImageUrls.has(srcStr) || badImageUrls.has(rawSrc)) {
                    throw new Error('URL marked bad; skipping additional fetch->dataURL attempts');
                  }
                  const dataUrl = await fetchImageAsDataUrl(srcStr, remainingBudget());
                  if (!dataUrl.startsWith('data:image/')) {
                    throw new Error('Non-image data URL returned');
                  }
                  const fmt = (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) ? 'JPEG' : 'PNG';
                  doc.addImage(
                    dataUrl,
                    fmt,
                    imageX,
                    imageY,
                    imageWidth,
                    imageHeight,
                    undefined,
                    'FAST'
                  );
                  
                  // Draw border around image if enabled
                  if (element.hasBorder) {
                    try {
                      const borderColor = element.borderColor || '#ffffff';
                      const borderWidth = (element.borderWidth || 0) + 8;
                      const borderOpacity = element.borderOpacity !== undefined ? element.borderOpacity / 100 : 1.0;
                      const borderRadius = element.borderRadius || 0;
                      const borderStyle = element.borderStyle || 'solid';
                      
                      doc.saveGraphicsState();
                      
                      // Set opacity if supported
                      try {
                        if (doc && typeof doc.setGState === 'function' && doc.GState) {
                          doc.setGState(new doc.GState({ opacity: borderOpacity }));
                        }
                      } catch (_) {}
                      
                      doc.setFillColor(255, 255, 255, 0); // No fill
                      
                      // Draw border with style support
                      const cornerRadius = borderRadius * scale;
                      drawImageBorder(doc, imageX, imageY, imageWidth, imageHeight, borderColor, borderWidth, borderStyle, cornerRadius, scale, hexToRgb, drawRoundedRect);
                      
                      doc.restoreGraphicsState();
                      console.log('[PDF Border] Drew border around fetch->dataURL image:', { borderColor, borderWidth, borderStyle, borderRadius });
                    } catch (borderErr) {
                      console.warn('Failed to draw image border:', borderErr);
                    }
                  }
                  
                  console.log('Image added via fetch->dataURL fallback');
                  added = true;
                } catch (e) {
                  console.warn('fetch->dataURL fallback failed, will try direct addImage or placeholder', e);
                  if (options?.fastSkipBrokenImages !== false) {
                    console.warn('Fast-skip enabled. Skipping image after late dataURL failure.');
                    abortRender = true;
                  }
                }
              }

              if (abortRender) { return; }

              if (!added) {
                // Fallback: add original image element with detected format
                if (taintedCanvas && imageToUse) {
                  try {
                    const srcW = imageToUse.naturalWidth || imageToUse.width || 0;
                    const srcH = imageToUse.naturalHeight || imageToUse.height || 0;
                    const srcPixels = srcW * srcH;
                    const extreme = srcPixels > (5000 * 5000); // >25MP considered unsafe
                    if (!extreme) {
                      const format = imageToUse.src.toLowerCase().includes('image/jpeg') ? 'JPEG' : 'PNG';
                      doc.addImage(
                        imageToUse,
                        format,
                        imageX,
                        imageY,
                        imageWidth,
                        imageHeight,
                        undefined,
                        'FAST'
                      );
                      
                      // Draw border around image if enabled
                      if (element.hasBorder) {
                        try {
                          const borderColor = element.borderColor || '#ffffff';
                          const borderWidth = (element.borderWidth || 0) + 4;
                          const borderOpacity = element.borderOpacity !== undefined ? element.borderOpacity / 100 : 1.0;
                          const borderRadius = element.borderRadius || 0;
                          const borderStyle = element.borderStyle || 'solid';
                          
                          doc.saveGraphicsState();
                          
                          // Set opacity if supported
                          try {
                            if (doc && typeof doc.setGState === 'function' && doc.GState) {
                              doc.setGState(new doc.GState({ opacity: borderOpacity }));
                            }
                          } catch (_) {}
                          
                          doc.setFillColor(255, 255, 255, 0); // No fill
                          
                          // Draw border with style support
                          const cornerRadius = borderRadius * scale;
                          drawImageBorder(doc, imageX, imageY, imageWidth, imageHeight, borderColor, borderWidth, borderStyle, cornerRadius, scale, hexToRgb, drawRoundedRect);
                          
                          doc.restoreGraphicsState();
                          console.log('[PDF Border] Drew border around direct element image:', { borderColor, borderWidth, borderStyle, borderRadius });
                        } catch (borderErr) {
                          console.warn('Failed to draw image border:', borderErr);
                        }
                      }
                      
                      added = true;
                      console.log('Image added via direct element add after other fallbacks');
                    }
                  } catch (addErr) {
                    console.warn('Direct element add after fallbacks failed:', addErr);
                  }
                }
              }

              if (!added) {
                // Final policy: skip broken image without drawing placeholder
                console.warn('Skipping broken/unreachable image without drawing placeholder:', rawSrc);
                added = true; // mark handled to continue page rendering
              }

              if (!added && imageToUse) {
                // Proceed with adding image element
                try {
                  const format = imageToUse.src.toLowerCase().includes('image/jpeg') ? 'JPEG' : 'PNG';
                  doc.addImage(
                    imageToUse,
                    format,
                    imageX,
                    imageY,
                    imageWidth,
                    imageHeight,
                    undefined,
                    'FAST'
                  );
                  added = true;
                  console.log(`Image added to PDF successfully (format: ${format})`);
                } catch (finalAddErr) {
                  console.warn('Final element addImage failed; skipping image render:', finalAddErr);
                }
              }
              
              // Debug information (guard against undefineds)
              if (options.debug) {
                const srcStr = (imageToUse && imageToUse.src) ? imageToUse.src.toLowerCase() : '';
                const dbgFormat = srcStr.includes('image/jpeg') ? 'JPEG' : (srcStr ? 'PNG' : 'unknown');
                console.log('Image details:', {
                  added,
                  format: dbgFormat,
                  dimensions: { width: imageWidth, height: imageHeight },
                  hadPreload: !!element._preloadedImage,
                  usedDataURL: srcStr.startsWith('data:image/'),
                });
              }
            } catch (imgError) {
              console.error('Error adding image to PDF:', imgError);
              return; // stop this element to avoid crashes
            }
          }
          
          // NO BORDERS

          console.log('Successfully added image to PDF');
        } catch (error) {
          console.error('Failed to add image:', error);
          return; // stop this element on error
        }
        break;

      case 'shape':
        try {
          console.log('Processing shape element:', element);
          
          // Get shape properties with fallbacks
          const fillColor = element.style?.fill || element.fill || '#000000';
          const opacity = parseFloat(element.style?.opacity || element.opacity || 1);
          const shapeType = element.shape || element.shapeType || 'rectangle';
          const borderRadius = parseInt(element.borderRadius || element.style?.borderRadius || 0);
          
          console.log('Shape properties:', { fillColor, opacity, shapeType, borderRadius });
          
          // Convert hex color to RGB
          let r, g, b;
          
          if (fillColor.startsWith('#')) {
            const hex = fillColor.replace(/^#/, '');
            if (hex.length === 3) {
              // Convert 3-digit hex to 6-digit
              r = parseInt(hex[0] + hex[0], 16);
              g = parseInt(hex[1] + hex[1], 16);
              b = parseInt(hex[2] + hex[2], 16);
            } else {
              r = parseInt(hex.substr(0, 2), 16);
              g = parseInt(hex.substr(2, 2), 16);
              b = parseInt(hex.substr(4, 2), 16);
            }
          } else if (fillColor.startsWith('rgb')) {
            // Extract RGB values from rgb/rgba format
            const rgbMatch = fillColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
            if (rgbMatch) {
              r = parseInt(rgbMatch[1], 10);
              g = parseInt(rgbMatch[2], 10);
              b = parseInt(rgbMatch[3], 10);
            } else {
              r = 0; g = 0; b = 0; // Default to black if parsing fails
            }
          } else {
            // Default colors for common names
            if (fillColor === 'red') { r = 255; g = 0; b = 0; }
            else if (fillColor === 'green') { r = 0; g = 128; b = 0; }
            else if (fillColor === 'blue') { r = 0; g = 0; b = 255; }
            else { r = 0; g = 0; b = 0; } // Default to black
          }
          
          // Set fill color and opacity
          doc.setFillColor(r, g, b);
          
          // Create a new graphics state for opacity
          if (opacity < 1) {
            const gState = new doc.GState({ opacity: opacity });
            doc.setGState(gState);
          }
          
          // Draw the shape based on its type
          if (shapeType === 'circle') {
            const centerX = x + (element.size.width * scale) / 2;
            const centerY = y + (element.size.height * scale) / 2;
            const radius = (Math.min(element.size.width, element.size.height) / 2) * scale;
            doc.circle(centerX, centerY, radius, 'F');
          } else if (shapeType === 'triangle') {
            // Draw triangle
            const width = element.size.width * scale;
            const height = element.size.height * scale;
            
            // Define the three points of the triangle
            const topX = x + width / 2;
            const topY = y;
            const bottomLeftX = x;
            const bottomLeftY = y + height;
            const bottomRightX = x + width;
            const bottomRightY = y + height;
            
            // Draw the triangle using a filled polygon
            doc.triangle(
              topX, topY,
              bottomLeftX, bottomLeftY,
              bottomRightX, bottomRightY,
              'F'
            );
           } else if ((shapeType === 'rectangle' || shapeType === 'roundedRect') && borderRadius > 0) {
             // Draw rounded rectangle
             const width = element.size.width * scale;
             const height = element.size.height * scale;
             const radius = Math.min(borderRadius * scale, width/2, height/2);
             
             // SKIP all border shapes - no borders rendered
             if (element._isBorderShape) {
               console.log('Skipping auto-generated border shape');
             } else {
              // Regular shape - draw filled
              drawRoundedRect(doc, x, y, width, height, radius, 'F');
            }
          } else {
            // Regular rectangle or other shapes default to rectangle
            doc.rect(
              x,
              y,
              element.size.width * scale,
              element.size.height * scale,
              'F'
            );
          }
          
          console.log('Shape rendered successfully');
        } catch (shapeError) {
          console.error('Error rendering shape:', shapeError, element);
          
          // Draw a fallback rectangle with error indication
          doc.setFillColor(255, 200, 200);
          doc.rect(
            x,
            y,
            element.size.width * scale,
            element.size.height * scale,
            'F'
          );
        }
        break;

      default:
        console.warn('Unknown element type:', element.type);
    }
  } catch (error) {
    console.error('Error rendering element:', error, element);
  }
}

// Convert hex color to RGB object
function hexToRgb(hex) {
  // Remove the hash if it exists
  hex = hex.replace(/^#/, '');
  
  // Parse the hex values
  let r, g, b;
  if (hex.length === 3) {
    // For shorthand hex colors like #FFF
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
  } else {
    // For standard hex colors like #FFFFFF
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }
  
  // Handle invalid hex colors
  r = isNaN(r) ? 0 : r;
  g = isNaN(g) ? 0 : g;
  b = isNaN(b) ? 0 : b;
  
  return { r, g, b };
}

// Draw a rectangle with rounded corners in jsPDF
function drawRoundedRect(doc, x, y, width, height, radius, style) {
  // If radius is 0 or not provided, just draw a regular rectangle
  if (!radius || radius === 0) {
    doc.rect(x, y, width, height, style);
    return;
  }
  
  // Ensure radius isn't too large
  const maxRadius = Math.min(width, height) / 2;
  radius = Math.min(radius, maxRadius);
  
  // Draw the rounded rectangle path
  doc.roundedRect(x, y, width, height, radius, radius, style);
}
